import type { PortalMeResponseDto } from '@supply-mover-2-0/shared';
import { authFetch, portalFetch } from '../lib/apiClient';
import { getBrandAlias } from '../lib/tenantResolver';

// --- Login server types (not shared DTOs since these are login-server-specific) ---

export interface TenantCheckResponse {
	valid: boolean;
	name: string;
	authOptions?: {
		phoneLoginEnabled: boolean;
	};
}

export interface PortalLoginResponse {
	accessToken: string;
	refreshToken: string;
	expiresIn: number;
	publicUserId: number;
	tenantAlias: string;
	languagePreference: string;
	/**
	 * PD-14144: when true, the portal password has aged past the
	 * `_passwordMaxAge_public` policy. No tokens are issued — the storefront
	 * sends the user into the login server's reset flow using `resetToken`.
	 */
	requiresPasswordChange?: boolean;
	resetToken?: string;
}

/**
 * Check whether a tenant alias maps to an active instance.
 * Returns the instance name for display and available auth options.
 * Throws on 404 (not found).
 */
export async function checkTenant(alias: string): Promise<TenantCheckResponse> {
	return authFetch<TenantCheckResponse>(`/tenant/${encodeURIComponent(alias)}`);
}

/**
 * Authenticate a portal user with email/password. Calls the login server.
 *
 * The login server prefers the brand parsed from the request hostname
 * (Origin/Referer/Host), but native shells route through CapacitorHttp,
 * which strips those headers — so the webview's storefront origin carries
 * no brand and the server would fall back to the tenant's DEFAULT company.
 * We therefore also send `brandAlias` (the build-baked brand natively, the
 * hostname-parsed brand on web) as an explicit fallback. This cannot
 * escalate access: the server still gates entry on the user's portal-user
 * binding (company + enabled client assignment).
 */
export async function login(
	email: string,
	password: string,
	tenantAlias: string,
): Promise<PortalLoginResponse> {
	return authFetch<PortalLoginResponse>('/login', {
		method: 'POST',
		body: JSON.stringify({
			email,
			password,
			tenantAlias,
			brandAlias: getBrandAlias(),
		}),
	});
}

/**
 * PD-16471 — which channel a sign-in code went out on. The login server
 * decides from the identifier: an email address gets an email, a phone
 * number a text.
 */
export type SignInCodeChannel = 'email' | 'sms';

export interface SignInCodeRequestResponse {
	success: boolean;
	/** Seconds until the code expires (900 = 15 minutes) */
	expiresIn: number;
	channel: SignInCodeChannel;
}

/**
 * PD-16471 — send a one-time sign-in code (with a tap-to-sign-in link in the
 * same message) to an email address or phone number. Always reports success,
 * so it cannot be used to find out which addresses have accounts.
 *
 * Brand is preferred from the request hostname server-side; `brandAlias` is
 * the native fallback (see `login` above).
 */
export async function requestSignInCode(
	identifier: string,
	tenantAlias: string,
): Promise<SignInCodeRequestResponse> {
	return authFetch<SignInCodeRequestResponse>('/code-login/request', {
		method: 'POST',
		body: JSON.stringify({
			identifier,
			tenantAlias,
			brandAlias: getBrandAlias(),
		}),
	});
}

/**
 * PD-16471 — verify a sign-in code and authenticate. Returns the same token
 * response as password sign-in.
 */
export async function verifySignInCode(
	identifier: string,
	code: string,
	tenantAlias: string,
): Promise<PortalLoginResponse> {
	return authFetch<PortalLoginResponse>('/code-login/verify', {
		method: 'POST',
		body: JSON.stringify({
			identifier,
			code,
			tenantAlias,
			brandAlias: getBrandAlias(),
		}),
	});
}

/**
 * PD-16471 — redeem a tap-to-sign-in link (from a code message or a welcome
 * text) and authenticate.
 */
export async function verifySignInLink(
	token: string,
	tenantAlias: string,
): Promise<PortalLoginResponse> {
	return authFetch<PortalLoginResponse>('/link-login/verify', {
		method: 'POST',
		body: JSON.stringify({
			token,
			tenantAlias,
			brandAlias: getBrandAlias(),
		}),
	});
}

/**
 * End the session on the login server (PD-16414): the access token is
 * evicted and the refresh token revoked, so a copy of either is worthless
 * once the shopper signs out. The server always answers 200.
 */
export async function logout(
	accessToken: string | null,
	refreshToken: string | null,
): Promise<{ success: boolean }> {
	return authFetch<{ success: boolean }>('/logout', {
		method: 'POST',
		body: JSON.stringify({ accessToken, refreshToken }),
	});
}

/**
 * Fetch the currently authenticated portal user profile, clients, and active client.
 */
export async function me(token: string): Promise<PortalMeResponseDto> {
	return portalFetch<PortalMeResponseDto>('/auth/me', token);
}

/**
 * Set the active client for the current portal session.
 * Returns the confirmed activeClientId.
 */
export async function selectClient(
	token: string,
	clientId: number,
): Promise<{ activeClientId: number }> {
	return portalFetch<{ activeClientId: number }>('/auth/select-client', token, {
		method: 'POST',
		body: JSON.stringify({ clientId }),
	});
}
